/**
 * theme.ts — Innspels eget tema. KILDEN, og den eneste filen fargene finnes i.
 *
 * Skrevet i M0.4 (2026-09-04) og erstatter tokens lånt fra Følget. Herfra
 * genererer `scripts/generate-tokens.ts` to utganger, som ALDRI redigeres:
 *
 *   design/wireframes/tokens.js   ESM `{ t, light }` — wireframes, senere RN-widgeten
 *   theme/generated/tokens.css    CSS-variabler — panelet (M2.1) og portalen (M1.5)
 *
 * Kjør `node scripts/generate-tokens.ts` etter hver endring her og commit begge
 * utgangene sammen med denne filen. `--check` er rød i CI hvis de spriker, og
 * generatoren måler `kontrastpar` under og NEKTER å skrive hvis et par faller
 * under WCAG AA — en senere justering kan ikke senke en verdi ubemerket.
 *
 * To paletter, med ulike roller (se «M0.4» i docs/tech-decisions.md):
 *
 *   light   panelets standardpalett, og portalens fallback. Panelet støtter
 *           BEGGE fra 2026-09-10 (M2.6): `auto` følger operativsystemet, og
 *           brukeren kan overstyre. Se «Adminpanelet støtter både lys og mørk
 *           palett» i docs/tech-decisions.md.
 *           MERK: linja sa til 2026-09-09 at mørk modus i panelet var «avvist,
 *           ikke utsatt». Det vedtaket ble aldri tatt — M0.4 valgte MERKE og
 *           PALETT blant tre kandidater, ikke lys mot mørk.
 *   t       fallback for widget og portal når vertsappen ikke sender egne
 *           tokens. Widgeten skal låne vertens flate — `init({ theme })`
 *           overstyrer denne. Hardkodede verdier er aldri alternativet.
 *
 * Skalaene (space, radius, font, …) er identiske i begge paletter med vilje:
 * bare farge skifter mellom lys og mørk, aldri mål. Nøkkelsettet er uendret fra
 * de lånte tokenene, slik at wireframene byttet uten strukturendring.
 */

export type Farger = {
  bg: string; surface: string; surfaceRaised: string;
  border: string; borderStrong: string; borderInteractive: string;
  textPrimary: string; textSecondary: string; textMuted: string; textDisabled: string;
  actionPrimary: string; onActionPrimary: string;
  actionSecondaryBorder: string; actionSecondaryText: string;
  actionText: string;
  brand: string; onBrand: string; brandFill: string; onBrandFill: string; brandBg: string;
  danger: string; dangerFill: string; onDangerFill: string; dangerBg: string;
  success: string; onSuccess: string; successBg: string;
  warning: string; onWarning: string; warningBg: string;
  info: string; onInfo: string; infoBg: string;
  neutral: string; onNeutral: string; neutralBg: string;
  scrim: string;
};

/* ------------------------------------------------------------------------ */
/* Merket                                                                    */
/* ------------------------------------------------------------------------ */

/**
 * «Ringen» — innspillet treffer i midten, svaret brer seg utover.
 * Tegningen ligger i theme/merke.svg; her står bare det koden trenger.
 * Merket er en STREKTEGNING: de to ytre ringene tåler ikke å fylles.
 */
export const merke = {
  fil: 'theme/merke.svg',
  viewBox: '0 0 32 32',
  /** Minste størrelse merket er tegnet for. Under dette brukes ordmerket alene. */
  minStorrelsePx: 16,
};

/* ------------------------------------------------------------------------ */
/* Skalaer — like i begge paletter                                           */
/* ------------------------------------------------------------------------ */

export const skala = {
  /** 4/8-grid. Ingen avstand utenfor denne skalaen. */
  space: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32, xxl: 48 },
  /** sm = tette kontroller · md = knapper og felter · lg = kort og sheets · pill = piller. */
  radius: { sm: 6, md: 8, lg: 12, pill: 999 },
  /**
   * Typehierarkiet, og standarden for hvilken størrelse som er UTGANGSPUNKTET.
   *
   * **`body` er standard** i panel, portal og widget. Er du i tvil om en tekst skal
   * være `body` eller `small`, er den `body`.
   *   `small`   ÉN meta-linje per rad, og etiketter i piller og på sporingslinjen.
   *   `tiny`    bare inne i piller og i `kbd`. Aldri hjelpetekst, aldri brødtekst.
   *
   * Regelen sto omvendt i praksis til 2026-09-19: UX-gjennomgangen målte 52 av 65
   * tekstnoder i innboksens hovedflate til 13 px eller mindre (F3), fordi `small` var
   * det man grep etter og `body` var unntaket. DD-67 løftet først beslutningsgrunnlaget
   * (Reach, frister, låsegrunner, løftet) og ble så løftet til denne standarden — se
   * DD-67 i `docs/design-decisions.md`. Taket måles per skjerm i gjennomgangene
   * (`.claude/skills/design-retning/references/gjennomgang.md`): høyst 35 % av
   * tekstnodene på en skjerm skal være ≤ 13 px.
   *
   * `display` er HELTEN på en skjerm, og det finnes nøyaktig én av den: tallet eller
   * datoen skjermen handler om — reach i saksdetaljen, svarfristen, «17 varsles» i
   * Rettet-dialogen, konklusjonens resultattall, hero-overskriften på produktsiden
   * (M5.1). Kommentaren her sa til 2026-09-19 «aldri i panelet eller widgeten»; den
   * regelen ga flater der alt var metadata i samme grå (F2, ingen helt på noen skjerm).
   * To `display` på samme skjerm er ingen helt — da er begge bare store.
   *
   * `hero` og `ingress` finnes bare for produktsiden (`site/`, R9.2, 2026-09-27): `hero` er
   * «Alle får svar.» og de andre korte sideoverskriftene, flytende fra `display` (gulvet på
   * telefon) opp til `hero`. `ingress` er ingressen under. Regelen fra M5.1 er presisert til én
   * overskrift i `hero`- eller `display`-størrelse per side — se «R9.2» i tech-decisions.
   */
  font: { hero: 64, display: 40, h1: 24, h2: 21, h3: 18, ingress: 18, body: 16, small: 13, tiny: 11 },
  lineHeight: { hero: 68, display: 48, h1: 30, h2: 27, h3: 24, ingress: 28, body: 24, small: 18, tiny: 16 },
  weight: { regular: '400', medium: '500', semibold: '600', bold: '700' },
  /**
   * Systemfont med vilje: panelet skal føles innfødt, og widgeten skal arve
   * vertens flate. Ingen webfont lastes noe sted. I React Native settes
   * fontFamily ALDRI — systemfonten er standard der.
   */
  family: { base: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, system-ui, sans-serif" },
  /** Subtil bevegelse: skala-pop og fargesveip. Aldri konfetti eller fullskjerm. */
  motion: { fast: 120, base: 200, slow: 320 },
  /**
   * Inline-ikonets mål. Ionicons tegner med `stroke-width="32px"` i en viewBox på
   * 512, altså 6,25 % av boksen — så strektykkelsen FØLGER målet: 20px gir 1,25px
   * strek, 16px gir nøyaktig 1,00px.
   *
   * `minste: 16` er derfor et gulv og ikke en smakssak. Under det kan ikke
   * nettleseren tegne streken; den antialiaseres til delvis gjennomsiktig grått, og
   * ikonet mister kontrast selv om fargetokenet ligger på AA. Panelet satte tidligere
   * `width: 1em`, som ga 13px i etiketter og 11px i piller — 0,81px og 0,69px strek.
   *
   * `inline: 20` og ikke 16 ved siden av brødtekst: bokstavene tegnes med fontens egen
   * strektykkelse, ikonet med en hårstrek, så et ikon på nøyaktig tekstens em-høyde
   * leser mindre enn teksten ved siden av.
   */
  icon: { inline: 20, minste: 16 },
  /**
   * Ikonnavnene (Ionicons) bor hos statusmappene under og i panelets `Ikon.tsx`; her
   * står bare de som er RESERVERT, så ingen tar dem til noe annet: `bug` er feilsporet
   * og `bulb` ønskesporet (DD-63), `flask` betaprogrammet, og `telescope` er reservert
   * til arbeidsrommet Hypoteser dersom modulen blir valgt (uavgjort i idébanken,
   * 2026-09-19). Reservasjonen koster ingenting og hindrer at lyspæren betyr to ting.
   */
  /** Minste trykkflate. Gjelder også mus og tastatur på web. */
  touch: 48,
  screenPadding: 24,
  hairline: 1,
  /**
   * Bruddpunktet der @innspel/web bytter fra modal til bunnark (wireframe
   * innsending-web.jsx: «< 768 CSS-px via matchMedia, aldri user-agent»). Kun i
   * tokens.css — React Native har ingen modal-variant, og wireframene tegner
   * begge former side om side.
   */
  breakpoint: 768,
  /**
   * Panelets ene bruddpunkt (DD-51): på og under dette blir todelt-visningen én kolonne
   * (liste, så detalj med «Tilbake til lista») og sidemenyen en topplinje. Panelet er et
   * skrivebordsverktøy (DD-11); dette er lesbarhet på tablet, ikke mobil først. Kun i
   * tokens.css; panelet leser variabelen og måler med matchMedia — aldri et tall i CSS.
   */
  breakpointSmal: 900,
  /**
   * Produktsiden (M5.1): største bredde på innholdet, og lesebredden for løpende
   * tekst. Kun i tokens.css. Panelet bruker `readingWidth` på tekstkolonnen i
   * saksdetaljen (DD-57, 2026-09-14): skallet fyller vinduet, teksten gjør det ikke.
   * Widgeten låner vertens.
   */
  contentWidth: 1120,
  readingWidth: 720,
  /**
   * Bredden på et A4-ark ved 96 dpi (210 mm), for «Én side» i rommet Hypoteser (H3.5): arket står i
   * full størrelse på skjermen, og utskriften er det samme arket. Kun i tokens.css.
   */
  paperWidth: 794,
  /**
   * Skjermen i rommet (H3.2, P5): faktoren hele flaten skaleres med, så setningen og tallet leses
   * fra bakerst i rommet — `stor` (1,75) fra `storFra` CSS-px bredde, `liten` (1,25) fra `litenFra`.
   * Flaten har én `display` (DD-67), og skaleringen ganger skalaens egne verdier; den lager aldri
   * egne px. Kun i tokens.css: skjermen i rommet er en rute i panelet, ikke i widgeten.
   */
  presentasjon: { stor: 1.75, liten: 1.25, storFra: 1920, litenFra: 1280 },
};

/* ------------------------------------------------------------------------ */
/* Palettene                                                                 */
/* ------------------------------------------------------------------------ */

/**
 * Lys — panelet. Nøytralene har en svak kald bias mot brand i stedet for å
 * være rene gråtoner.
 *
 * brand er «Fjord», dyp fjordgrønn: statusene eier allerede grønn, rød, amber,
 * blå og grå, og teal er den eneste plassen på fargesirkelen der brand ikke
 * låner uttrykk fra en status. Se «M0.4» i docs/tech-decisions.md.
 */
export const light: Farger = {
  bg: '#F6F7F9',
  surface: '#FFFFFF',
  surfaceRaised: '#EDEFF3',

  border: '#D2D7DE',
  borderStrong: '#B9C0C9',
  borderInteractive: '#6F7883',

  textPrimary: '#14181D',
  textSecondary: '#454D57',
  textMuted: '#5C6470',
  /** Bevisst under AA. Kun for deaktiverte kontroller, aldri for lesbar tekst. */
  textDisabled: '#9AA2AD',

  actionPrimary: '#0A6673',
  onActionPrimary: '#FFFFFF',
  actionSecondaryBorder: '#6F7883',
  actionSecondaryText: '#14181D',
  /**
   * Tekstknappen: en HANDLING uten flate og uten ramme — «Ligner, men ikke helt», «Legg
   * til mer», «Fjern», «Vis»/«Skjul», «Kan ikke skanne?». Den skal lese som merkefargen,
   * slik konseptsidene tegnet den, og ikke som brødtekst.
   *
   * Skilt ut i R8.4 (2026-09-20) fordi disse knappene sto i `actionSecondaryText`, som i
   * LYS palett er identisk med `textPrimary` — en tekstknapp ved siden av en tonet knapp
   * leste da som vanlig tekst (R8.3-noten punkt 4). `actionSecondaryText` er teksten i
   * sekundærknappen, den med ramme, og er IKKE en handling i merkefargen; den blir
   * stående der en tekstknapp bevisst ikke skal være et forslag — «Gå ut av programmet»
   * og andre forlat-handlinger (DD-53: den røde er dialogen, ikke utgangen).
   *
   * Verdien er merkefargen i begge paletter, men tokenet er sitt eget: en justering av
   * `brand` som flate skal ikke kunne senke lesbarheten på en knapp ubemerket. Målt mot
   * `bg`, `surface` og `surfaceRaised` i `kontrastpar`.
   */
  actionText: '#0A6673',

  brand: '#0A6673',
  onBrand: '#FFFFFF',
  brandFill: '#0A6673',
  onBrandFill: '#FFFFFF',
  brandBg: '#E0F0F2',

  /** danger som tekst, ikon og ramme; dangerFill kun for fylt destruktiv knapp. */
  danger: '#B3001B',
  dangerFill: '#B3001B',
  onDangerFill: '#FFFFFF',
  dangerBg: '#FCE8EA',

  success: '#04693C',
  onSuccess: '#FFFFFF',
  successBg: '#E4F4EC',

  warning: '#7A5200',
  onWarning: '#FFFFFF',
  warningBg: '#FBF1DE',

  info: '#00588F',
  onInfo: '#FFFFFF',
  infoBg: '#E4F0F8',

  neutral: '#5C6470',
  onNeutral: '#FFFFFF',
  neutralBg: '#EEF0F3',

  scrim: 'rgba(10, 14, 18, 0.45)',
};

/** Mørk — fallback for widget og portal. Aldri panelet. */
export const t: Farger = {
  bg: '#111417',
  surface: '#1A1E22',
  surfaceRaised: '#232830',

  border: '#2E343B',
  borderStrong: '#3D444D',
  borderInteractive: '#7C8894',

  textPrimary: '#F2F4F6',
  textSecondary: '#B4BCC5',
  textMuted: '#8D96A1',
  textDisabled: '#5A626B',

  actionPrimary: '#5AD3E2',
  onActionPrimary: '#04252A',
  actionSecondaryBorder: '#7C8894',
  actionSecondaryText: '#F2F4F6',
  /** Tekstknappen — se kommentaren i `light`. Merkefargen også her. */
  actionText: '#5AD3E2',

  brand: '#5AD3E2',
  onBrand: '#04252A',
  brandFill: '#0E7C8C',
  onBrandFill: '#FFFFFF',
  brandBg: '#0C2A30',

  danger: '#FF7A8C',
  dangerFill: '#C4001F',
  onDangerFill: '#FFFFFF',
  dangerBg: '#2C1116',

  success: '#3ED88A',
  onSuccess: '#062015',
  successBg: '#0E2A1E',

  warning: '#FFC24D',
  onWarning: '#2A1C02',
  warningBg: '#2C2107',

  info: '#5CC0FF',
  onInfo: '#04222F',
  infoBg: '#0E2531',

  neutral: '#A3ACB6',
  onNeutral: '#111417',
  neutralBg: '#242A31',

  scrim: 'rgba(0, 0, 0, 0.72)',
};

/* ------------------------------------------------------------------------ */
/* Statusmappene                                                             */
/* ------------------------------------------------------------------------ */

/**
 * Farge bærer aldri mening alene (DD-08): hver status har ikon OG tekstetikett
 * OG farge. `fg` og `bg` er TOKENNAVN, ikke verdier — de slås opp i den aktive
 * paletten, så samme mappe virker i lys og mørk.
 *
 * `variant` skiller statuser som deler farge: «Planlagt» og «Retter nå» er
 * begge brand, og skilles på kontur mot fylt — aldri på nyanse.
 *
 * `etikett` er navnet innsenderen ser, og velges per spor (DD-22): samme flyt,
 * ulike ord. Tabellen i docs/domain-model.md er kilden.
 *
 * `lofte` er SETNINGEN innsenderen ser for statusen (DD-08), også per spor. Den
 * sto tre steder før 2026-09-14 — i widgetens `tekster.ts`, i portalens og i
 * panelets `løfte()` — og spriket. Nå står den her, og alle tre leser den
 * genererte utgangen. `{frist}` i `vurderes` byttes med svarfristen som DATO
 * (DD-28); det er produktets eneste tidsløfte, og aldri «innen 90 dager».
 */
export type StatusOppforing = {
  statusType: 'reviewing' | 'active' | 'complete' | 'closed';
  ikon: string;
  etikett: { feil: string; onske: string };
  lofte: { feil: string; onske: string };
  fg: keyof Farger;
  bg: keyof Farger;
  variant: 'outline' | 'filled' | 'dashed';
};

/** Nøklene er `Submissions.SubmissionStatus` i contracts/, ordrett. */
export const submissionStatus: Record<string, StatusOppforing> = {
  ny: { statusType: 'reviewing', ikon: 'ellipse-outline',
    etikett: { feil: 'Mottatt', onske: 'Mottatt' },
    lofte: { feil: 'Vi har fått den. Du hører fra oss.', onske: 'Vi har fått den. Du hører fra oss.' },
    fg: 'textSecondary', bg: 'neutralBg', variant: 'outline' },

  vurderes: { statusType: 'reviewing', ikon: 'hourglass-outline',
    etikett: { feil: 'Vurderes', onske: 'Vurderes' },
    lofte: { feil: 'Svar innen {frist} — også hvis det er nei.', onske: 'Svar innen {frist} — også hvis det er nei.' },
    fg: 'info', bg: 'infoBg', variant: 'outline' },

  planlagt: { statusType: 'active', ikon: 'calendar-outline',
    etikett: { feil: 'Planlagt', onske: 'Planlagt' },
    lofte: { feil: 'Du får beskjed når den er rettet.', onske: 'Du får beskjed når det er levert.' },
    fg: 'brand', bg: 'brandBg', variant: 'outline' },

  bygges: { statusType: 'active', ikon: 'construct-outline',
    etikett: { feil: 'Retter nå', onske: 'Bygger' },
    lofte: { feil: 'Du får beskjed når den er rettet.', onske: 'Du får beskjed når det er levert.' },
    fg: 'brand', bg: 'brandBg', variant: 'filled' },

  kjent_feil: { statusType: 'active', ikon: 'warning-outline',
    etikett: { feil: 'Kjent feil', onske: 'Kjent feil' },
    lofte: { feil: 'Kjent feil. Du får beskjed når den er rettet.', onske: 'Kjent feil. Du får beskjed når den er rettet.' },
    fg: 'warning', bg: 'warningBg', variant: 'outline' },

  levert: { statusType: 'complete', ikon: 'checkmark-circle-outline',
    etikett: { feil: 'Rettet', onske: 'Levert' },
    lofte: { feil: 'Ferdig. Ser du feilen igjen, send et nytt innspill.', onske: 'Ferdig. Takk for ønsket.' },
    fg: 'success', bg: 'successBg', variant: 'outline' },

  avvist: { statusType: 'closed', ikon: 'close-circle-outline',
    etikett: { feil: 'Avvist', onske: 'Avvist' },
    lofte: { feil: 'Vi har svart på denne.', onske: 'Vi har svart på denne.' },
    fg: 'danger', bg: 'dangerBg', variant: 'outline' },

  duplikat: { statusType: 'closed', ikon: 'git-merge-outline',
    etikett: { feil: 'Slått sammen', onske: 'Slått sammen' },
    lofte: { feil: 'Slått sammen med et innspill som ligner.', onske: 'Slått sammen med et innspill som ligner.' },
    fg: 'neutral', bg: 'neutralBg', variant: 'outline' },
};

/**
 * Løftet for `vurderes` NÅR fristen ikke finnes. `{frist}`-malen kan ikke stå
 * med en tom plassholder på skjermen, og et innspill uten frist har fortsatt et
 * løfte om svar (DD-07) — bare ikke et tidsløfte (DD-28).
 */
export const lofteUtenFrist = 'Du får svar — også hvis det er nei.';

/**
 * Programmets tilstandsmaskin. Nøklene er `Programs.ProgramStatus` i
 * contracts/, ordrett. `avbrutt` er den eneste fylte og `utkast` den eneste
 * stiplede — formen skiller dem, ikke bare fargen.
 *
 * Etiketten er ETT ord (program-konsept v0.1, P2, produkteier 2026-09-19): leddet
 * «invitasjoner kan sendes» / «oppfølginger går ut» / «rapport fryst» sto i pillen
 * og gjentok det sporingslinjens nå-ledd og helten sier (DD-08: samme opplysning
 * to steder). Leddet bor nå i sporingslinjen på programsiden, ikke her.
 */
export type ProgramStatusOppforing = {
  ikon: string;
  etikett: string;
  fg: keyof Farger;
  bg: keyof Farger;
  variant: 'outline' | 'filled' | 'dashed';
};

export const programStatus: Record<string, ProgramStatusOppforing> = {
  utkast: { ikon: 'create-outline', etikett: 'Utkast',
    fg: 'textSecondary', bg: 'neutralBg', variant: 'dashed' },
  aapen: { ikon: 'ellipse-outline', etikett: 'Åpen',
    fg: 'info', bg: 'infoBg', variant: 'outline' },
  aktiv: { ikon: 'checkmark-circle-outline', etikett: 'Aktiv',
    fg: 'success', bg: 'successBg', variant: 'outline' },
  avsluttes: { ikon: 'time-outline', etikett: 'Avsluttes',
    fg: 'warning', bg: 'warningBg', variant: 'outline' },
  avsluttet: { ikon: 'checkmark-circle-outline', etikett: 'Avsluttet',
    fg: 'neutral', bg: 'neutralBg', variant: 'outline' },
  /* Konvensjonen er lik for alle: `fg` er aksentfargen, `bg` flaten bak den — og en `filled`
     pille BYTTER dem (aksent som flate, `bg` som tekst), slik `bygges` alt gjør. `avbrutt`
     var skrevet omvendt og ble rød tekst på hvit flate uten pille (UX-gjennomgangen
     2026-09-14, B4). */
  avbrutt: { ikon: 'close-circle', etikett: 'Avbrutt',
    fg: 'dangerFill', bg: 'onDangerFill', variant: 'filled' },
};

/**
 * Kjent feil-registeret (M2.4). Nøklene er `KnownIssues.KnownIssueStatus` i
 * contracts/, ordrett. `fikset` heter «Rettet» for mennesker (DD-22) og er
 * stiplet: oppføringen er historikk for de som har oppdatert, men widgeten
 * viser den fortsatt for appVersion ≤ fixed_in (DD-30). Samme form som
 * programstatusen — én etikett, ikke én per spor.
 */
export const knownIssueStatus: Record<string, ProgramStatusOppforing> = {
  aktiv: { ikon: 'warning-outline', etikett: 'Aktiv',
    fg: 'warning', bg: 'warningBg', variant: 'outline' },
  fikset: { ikon: 'checkmark-circle-outline', etikett: 'Rettet',
    fg: 'success', bg: 'successBg', variant: 'dashed' },
};

/**
 * Hypotesens statuser (H2.3, DD-22 presisert 2026-09-25). Nøklene er
 * `Hypotheses.HypothesisStatusKey` i contracts/, ordrett: tretten nøkler for elleve
 * statuser, fordi `konkludert` har ett navn per utfall.
 *
 * Her står bare det som er PLATTFORMENS: kategorien (maskinens seks), ikonet og fargen.
 * Navnet på pillen og løftet under den er leietakerens (`HypothesisStatusWording` i
 * `GET /hypotheses/config`, H24) og står aldri her — en komponent som skrev «Vurderes»
 * selv, ville vist feil ord hos en leietaker som har kalt den noe annet.
 *
 * Formen er forslaget i `design/wireframes/admin-hypoteser.jsx` (STATUS, DD-22):
 * draft stiplet uten flate · reviewing info · waiting warning · active brand · complete
 * nøytral, de tre utfallene med samme farge og vekt (avkreftet er ikke en feil, H20) ·
 * closed nøytral, bortsett fra Avvist i danger. Testes har begeret, aldri kolben — den er
 * betaprogrammets (DD-63). Valgt deler ikon med sakens «Levert» og avkreftet med «Ingen» i
 * Panelbrukere; de står i hvert sitt rom, og ordet ved siden av skiller dem (DD-08).
 */
export type HypoteseStatusOppforing = {
  statusType: 'draft' | 'reviewing' | 'waiting' | 'active' | 'complete' | 'closed';
  ikon: string;
  fg: keyof Farger;
  bg: keyof Farger;
  variant: 'outline' | 'filled' | 'dashed';
};

export const hypothesisStatus: Record<string, HypoteseStatusOppforing> = {
  utkast: { statusType: 'draft', ikon: 'document-outline', fg: 'textSecondary', bg: 'bg', variant: 'dashed' },
  mottatt: { statusType: 'reviewing', ikon: 'ellipse-outline', fg: 'info', bg: 'infoBg', variant: 'outline' },
  vurderes: { statusType: 'reviewing', ikon: 'hourglass-outline', fg: 'info', bg: 'infoBg', variant: 'outline' },
  parkert: { statusType: 'waiting', ikon: 'bookmark-outline', fg: 'warning', bg: 'warningBg', variant: 'outline' },
  pa_pause: { statusType: 'waiting', ikon: 'pause-circle-outline', fg: 'warning', bg: 'warningBg', variant: 'outline' },
  valgt: { statusType: 'active', ikon: 'checkmark-circle-outline', fg: 'brand', bg: 'brandBg', variant: 'outline' },
  testes: { statusType: 'active', ikon: 'beaker-outline', fg: 'brand', bg: 'brandBg', variant: 'outline' },
  bekreftet: { statusType: 'complete', ikon: 'add-circle-outline', fg: 'textSecondary', bg: 'neutralBg', variant: 'outline' },
  delvis: { statusType: 'complete', ikon: 'pie-chart-outline', fg: 'textSecondary', bg: 'neutralBg', variant: 'outline' },
  avkreftet: { statusType: 'complete', ikon: 'remove-circle-outline', fg: 'textSecondary', bg: 'neutralBg', variant: 'outline' },
  avvist: { statusType: 'closed', ikon: 'close-circle-outline', fg: 'danger', bg: 'dangerBg', variant: 'outline' },
  stoppet: { statusType: 'closed', ikon: 'stop-circle-outline', fg: 'textSecondary', bg: 'neutralBg', variant: 'outline' },
  trukket: { statusType: 'closed', ikon: 'arrow-undo-outline', fg: 'textSecondary', bg: 'neutralBg', variant: 'outline' },
  // H3.3 (overgang 13): fletteikonet fra konseptsiden, nøytral som de andre lukkede.
  slaatt_sammen: { statusType: 'closed', ikon: 'git-merge-outline', fg: 'textSecondary', bg: 'neutralBg', variant: 'outline' },
};

/**
 * Kildens status (H3.3, `Hypotheses.SourceStatus` i contracts/): på en katalogkilde og på en
 * hypotese. Avklart i tekstfargen; uklar og må bygges i warning — hver med ikon OG etikett
 * (DD-08), aldri farge alene. `etikett` er den hele setningen i detaljen og kildekortet, `kort`
 * er meta-linjens ord i katalogens rad.
 */
export type KildestatusOppforing = { ikon: string; fg: keyof Farger; etikett: string; kort: string };

export const sourceStatus: Record<string, KildestatusOppforing> = {
  verifisert: { ikon: 'shield-checkmark-outline', fg: 'textPrimary', etikett: 'Definisjonen er avklart', kort: 'Avklart' },
  uavklart_definisjon: { ikon: 'help-circle-outline', fg: 'warning', etikett: 'Uklar definisjon', kort: 'Uklar definisjon' },
  maa_bygges: { ikon: 'hammer-outline', fg: 'warning', etikett: 'Må bygges', kort: 'Må bygges' },
};

/**
 * Møtets tilstand (H3.2, DD-08 presisert 2026-10-29): en egen mappe, fordi et møte ikke er en
 * hypotese og «Bekreftet» ellers ville lånt løftet til en status det ikke har. Nøklene er
 * `Hypotheses.DecisionMeetingStatus` i contracts/. Hver har ikon OG etikett; fargen bærer aldri
 * mening alene. `etikettFor` er ordet for den som skal bekrefte — «Venter på deg» — mens `etikett`
 * er det alle andre ser.
 */
export type MoeteStatusOppforing = { ikon: string; fg: keyof Farger; bg: keyof Farger; etikett: string; etikettFor?: string };

export const meetingStatus: Record<string, MoeteStatusOppforing> = {
  planlagt: { ikon: 'calendar-outline', fg: 'info', bg: 'infoBg', etikett: 'Planlagt' },
  venter_bekreftelse: { ikon: 'time-outline', fg: 'warning', bg: 'warningBg', etikett: 'Venter på bekreftelse', etikettFor: 'Venter på deg' },
  bekreftet: { ikon: 'checkmark-circle-outline', fg: 'success', bg: 'successBg', etikett: 'Bekreftet' },
};

/**
 * Utfallet på en møterad (H3.2, `Hypotheses.MeetingOutcomeKind`): ikonet og etiketten er
 * plattformens, fargen statusens den fører til (DD-58: Velg i valgts farge, Avvis i avvists).
 * «Ikke i dag» har ikon og etikett, men ingen farge — den lover innsenderen ingenting (DD-08).
 * `navn` er knappen i kjøringen, `etikett` pillen etterpå, `tast` tasten (V P A I).
 */
export type MoeteUtfallOppforing = { ikon: string; fg: keyof Farger; bg: keyof Farger; navn: string; etikett: string; tast: string };

export const meetingOutcome: Record<string, MoeteUtfallOppforing> = {
  velg: { ikon: 'checkmark-circle-outline', fg: 'brand', bg: 'brandBg', navn: 'Velg', etikett: 'Valgt', tast: 'v' },
  parker: { ikon: 'bookmark-outline', fg: 'warning', bg: 'warningBg', navn: 'Parker til …', etikett: 'Parkert', tast: 'p' },
  avvis: { ikon: 'close-circle-outline', fg: 'danger', bg: 'dangerBg', navn: 'Avvis', etikett: 'Avvist', tast: 'a' },
  ikke_i_dag: { ikon: 'play-skip-forward-outline', fg: 'textSecondary', bg: 'neutralBg', navn: 'Ikke i dag', etikett: 'Ikke i dag', tast: 'i' },
};

/* ------------------------------------------------------------------------ */
/* Unntaket: skjermbilde-forhåndsvisningen                                   */
/* ------------------------------------------------------------------------ */

/**
 * Det ene stedet temaet ikke gjelder. Bildet vises i sine EGNE farger, og
 * maskeringen tegnes i faste signalfarger uavhengig av palett — brukeren må se
 * hva som er dekket, også når skjermbildet selv er teal eller rosa.
 *
 * Magenta er valgt fordi den ikke finnes i noen av palettene og er sjelden i
 * app-skjermbilder. Automatisk maskering av private views (DD-39) er mørk med
 * magenta kant, så den skilles fra brukerens eget penselstrøk uten å bli
 * mindre synlig. M1.3 eier resten av maskeringsflaten.
 */
export const screenRules = {
  /** Brukerens penselstrøk. */
  brushFill: '#E5007D',
  /** Private views — plattformen maskerte dette selv, og det har forrang. */
  autoMaskFill: '#101114',
  autoMaskStroke: '#E5007D',
  /**
   * Etiketten «Maskert av appen» PÅ den maskerte flaten. Den sto i
   * `textSecondary` og ble målt til ~2,2:1 i lys palett (UX-gjennomgangen C11a):
   * en mørk tekst på en nesten svart boks, utenfor palettene og derfor utenfor
   * kontrastvakten. Den måles nå — se `skjermKontrastpar` under.
   */
  autoMaskText: '#FFFFFF',
  /** Flaten bak bildet i forhåndsvisningen. */
  previewBackdrop: '#000000',
  /** Flaten bak QR-koden i tofaktor-oppsettet: alltid hvit, også i mørk palett — ellers er koden uskannbar. */
  qrBackdrop: '#FFFFFF',
  /** Penselens bredde i punkter («Tegn over», DD-40). Bred nok til å dekke en tekstlinje i ett strøk. */
  brushWidth: 28,
  /**
   * «Masker all tekst» (DD-40, presisert for RN): heldekkende uskarphet i punkter.
   * 24 gjør brødtekst uleselig på 3×-skjermer; bildet re-enkodes som JPEG etterpå.
   */
  textBlurRadius: 24,
};

/* ------------------------------------------------------------------------ */
/* Kontraktene generatoren håndhever                                         */
/* ------------------------------------------------------------------------ */

/**
 * Parene generatoren måler ved hver kjøring. Faller ett under `krav`, skrives
 * ingenting og kjøringen feiler — dette er hele grunnen til at fargene ikke
 * kan justeres «bare litt» senere.
 *
 * 4.5 er WCAG AA for normal tekst; 3 er AA for grafiske kontroller og
 * ikke-tekstlig innhold. `border` og `borderStrong` står IKKE her: de er
 * dekorative. Trykkbare rammer og grafikk som bærer mening (sporingslinjens
 * stiplede node og strek for det som kommer, K6 2026-09-27) bruker
 * `borderInteractive`, og den måles — mot HVER flate den kan havne på, ikke bare
 * mot `bg`.
 *
 * `textDisabled` står heller ikke her, med vilje: den er bevisst under AA.
 */
export const kontrastpar: Array<{ fg: keyof Farger; bakgrunn: keyof Farger; krav: number; hva: string }> = [
  { fg: 'textPrimary', bakgrunn: 'bg', krav: 4.5, hva: 'Brødtekst på sidebakgrunn' },
  { fg: 'textPrimary', bakgrunn: 'surface', krav: 4.5, hva: 'Brødtekst på kort' },
  { fg: 'textPrimary', bakgrunn: 'surfaceRaised', krav: 4.5, hva: 'Brødtekst på hevet flate' },
  { fg: 'textSecondary', bakgrunn: 'bg', krav: 4.5, hva: 'Sekundærtekst på sidebakgrunn' },
  { fg: 'textSecondary', bakgrunn: 'surface', krav: 4.5, hva: 'Sekundærtekst på kort' },
  { fg: 'textSecondary', bakgrunn: 'surfaceRaised', krav: 4.5, hva: 'Sekundærtekst på hevet flate' },
  { fg: 'textMuted', bakgrunn: 'bg', krav: 4.5, hva: 'Hjelpetekst på sidebakgrunn' },
  { fg: 'textMuted', bakgrunn: 'surface', krav: 4.5, hva: 'Hjelpetekst på kort' },
  { fg: 'textMuted', bakgrunn: 'surfaceRaised', krav: 4.5, hva: 'Hjelpetekst på hevet flate' },

  { fg: 'onActionPrimary', bakgrunn: 'actionPrimary', krav: 4.5, hva: 'Tekst i fylt primærknapp' },
  { fg: 'actionSecondaryText', bakgrunn: 'bg', krav: 4.5, hva: 'Tekst i sekundærknapp' },
  { fg: 'actionSecondaryBorder', bakgrunn: 'bg', krav: 3, hva: 'Ramme på sekundærknapp' },
  /* Tekstknappen har verken flate eller ramme, så fargen er ALT som skiller den fra
     brødteksten rundt (R8.4). Den står på alle tre flatene — i arket, i et kort og på en
     valgt rad — og måles mot hver av dem, som `borderInteractive` og `warning`. */
  { fg: 'actionText', bakgrunn: 'bg', krav: 4.5, hva: 'Tekstknapp på sidebakgrunn' },
  { fg: 'actionText', bakgrunn: 'surface', krav: 4.5, hva: 'Tekstknapp på kort' },
  { fg: 'actionText', bakgrunn: 'surfaceRaised', krav: 4.5, hva: 'Tekstknapp på hevet flate' },

  { fg: 'onBrand', bakgrunn: 'brand', krav: 4.5, hva: 'Tekst på brand-flate' },
  { fg: 'onBrandFill', bakgrunn: 'brandFill', krav: 4.5, hva: 'Tekst på fylt brand-flate' },
  { fg: 'brand', bakgrunn: 'bg', krav: 4.5, hva: 'Brand som tekst på sidebakgrunn' },
  { fg: 'brand', bakgrunn: 'surface', krav: 4.5, hva: 'Brand som tekst på kort' },
  { fg: 'brand', bakgrunn: 'brandBg', krav: 4.5, hva: 'Brand i egen tonet flate' },

  { fg: 'onDangerFill', bakgrunn: 'dangerFill', krav: 4.5, hva: 'Tekst i fylt destruktiv knapp' },
  { fg: 'danger', bakgrunn: 'bg', krav: 4.5, hva: '«Avvist» på sidebakgrunn' },
  { fg: 'danger', bakgrunn: 'surface', krav: 4.5, hva: '«Avvist» på kort' },
  { fg: 'danger', bakgrunn: 'dangerBg', krav: 4.5, hva: '«Avvist» i egen pille' },

  { fg: 'onSuccess', bakgrunn: 'success', krav: 4.5, hva: 'Tekst på success-flate' },
  { fg: 'success', bakgrunn: 'surface', krav: 4.5, hva: '«Rettet» på kort' },
  { fg: 'success', bakgrunn: 'successBg', krav: 4.5, hva: '«Rettet» i egen pille' },

  { fg: 'onWarning', bakgrunn: 'warning', krav: 4.5, hva: 'Tekst på warning-flate' },
  { fg: 'warning', bakgrunn: 'surface', krav: 4.5, hva: '«Kjent feil» på kort' },
  { fg: 'warning', bakgrunn: 'warningBg', krav: 4.5, hva: '«Kjent feil» i egen pille' },
  /* «Over frist» er `warning` fra 2026-09-19 (DD-53), og står som ren tekst med ikon i
     listen — ikke i en pille. Listen ligger på `bg`, og den valgte raden på
     `surfaceRaised`; begge flatene måles, som for `borderInteractive`. */
  { fg: 'warning', bakgrunn: 'bg', krav: 4.5, hva: '«Over frist» på sidebakgrunn' },
  { fg: 'warning', bakgrunn: 'surfaceRaised', krav: 4.5, hva: '«Over frist» på valgt rad' },

  { fg: 'onInfo', bakgrunn: 'info', krav: 4.5, hva: 'Tekst på info-flate' },
  { fg: 'info', bakgrunn: 'surface', krav: 4.5, hva: '«Vurderes» på kort' },
  { fg: 'info', bakgrunn: 'infoBg', krav: 4.5, hva: '«Vurderes» i egen pille' },

  /* Hypotesene (H2.3): innboksen i rommet Hypoteser ligger på `bg`, den valgte raden på
     `surfaceRaised`, og radens statusikon og meta-linjens statusord står i statusens farge —
     «Mottatt» og «Vurderes» er `info`. De avsluttede statusene står i `textSecondary` på
     `neutralBg` i pillen, et par `submissionStatus.ny` alt brukte uten å være målt. */
  { fg: 'info', bakgrunn: 'bg', krav: 4.5, hva: '«Vurderes» på sidebakgrunn' },
  { fg: 'info', bakgrunn: 'surfaceRaised', krav: 4.5, hva: '«Vurderes» på valgt rad' },
  { fg: 'textSecondary', bakgrunn: 'neutralBg', krav: 4.5, hva: '«Bekreftet» og «Trukket» i egen pille' },

  { fg: 'onNeutral', bakgrunn: 'neutral', krav: 4.5, hva: 'Tekst på neutral-flate' },
  { fg: 'neutral', bakgrunn: 'surface', krav: 4.5, hva: '«Slått sammen» på kort' },
  { fg: 'neutral', bakgrunn: 'neutralBg', krav: 4.5, hva: '«Slått sammen» i egen pille' },

  { fg: 'borderInteractive', bakgrunn: 'bg', krav: 3, hva: 'Trykkbar ramme og kommende ledd på sidebakgrunn' },
  { fg: 'borderInteractive', bakgrunn: 'surface', krav: 3, hva: 'Trykkbar ramme og kommende ledd på kort' },
  { fg: 'borderInteractive', bakgrunn: 'surfaceRaised', krav: 3, hva: 'Trykkbar ramme og kommende ledd på hevet flate' },
];

/**
 * Par som IKKE kan skilles på farge alene, målt i M0.4 og notert her fordi
 * ferdigheten `brand-ui-guidelines` krever at avstanden er målt før begge tas
 * i bruk på samme flate.
 *
 * brand↔info er 1.13 (lys) og 1.14 (mørk); brand↔danger er 1.08 og 1.41.
 * Kontrastforhold måler LUMINANS, ikke kulør — fargene ser tydelig forskjellige
 * ut for de fleste, men er nær identiske i gråtoner og for rød-grønn svakhet.
 * Ingen palett løser dette; det er nettopp derfor DD-08 krever ikon og etikett.
 * Generatoren skriver de målte verdiene inn i utgangene ved hver kjøring.
 */
export const ikkeSkillbarePaaFargeAlene = [
  { a: 'brand', b: 'info' },
  { a: 'brand', b: 'danger' },
];

/**
 * Kontrastpar UTENFOR palettene: skjermbilde-forhåndsvisningen har sine egne,
 * faste farger (`screenRules`), og de gikk derfor klar av vakten over.
 *
 * «Maskert av appen»-etiketten sto i `textSecondary` på `autoMaskFill` og ble
 * målt til ~2,2:1 i lys palett (UX-gjennomgangen C11a) — en etikett som forteller
 * at noe er beskyttet, og som ikke kunne leses. Generatoren måler disse parene
 * som alle andre og nekter å skrive under kravet.
 */
export const skjermKontrastpar: Array<{ fg: keyof typeof screenRules; bakgrunn: keyof typeof screenRules; krav: number; hva: string }> = [
  { fg: 'autoMaskText', bakgrunn: 'autoMaskFill', krav: 4.5, hva: '«Maskert av appen» på maskert felt' },
];
